/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import type { StrapiLogoList, StrapiMedia } from "@/types/strapi";

import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Link } from "@/ui/atoms/link";
import { Picture } from "@/ui/atoms/picture";
import { Text } from "@/ui/atoms/text";

import { buildStylesByBreakpoint } from "@/util/style_util";
import { getSectionGap } from "@/util/tokens/layout_util";
import { convertToRem } from "@/util/ui_util";

interface LogoListProps extends StrapiLogoList {}

export function LogoList(props: LogoListProps) {
    /**
     * Styles
     */
    const containerStyles = css({
        marginTop: Spacing["spacing-6"],
    });

    const logoListStyles = css(
        {
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
        },
        buildStylesByBreakpoint("gap", {
            extraSmall: Spacing["spacing-8"],
            medium: Spacing["spacing-18"],
        }),
    );

    const logoStyles = css(
        {
            display: "block",
            width: "auto",
        },
        buildStylesByBreakpoint("height", {
            extraSmall: convertToRem(20),
            small: convertToRem(24),
            medium: convertToRem(32),
            large: convertToRem(48),
        }),
    );

    /**
     * Rendering
     */

    const renderLogo = (logo: StrapiMedia) => {
        if (logo.caption) {
            return (
                <Link href={logo.caption} newWindow={true}>
                    <Picture
                        {...logo}
                        borderRadius="borderNone"
                        className={logoStyles}
                    />
                </Link>
            );
        }

        return (
            <Picture
                {...logo}
                borderRadius="borderNone"
                className={logoStyles}
            />
        );
    };

    return (
        <GridContainer
            legacyGrid={false}
            marginBottom={getSectionGap(props.Section_Gap)}
        >
            <GridColumn>
                <Text
                    shouldBalanceWrap
                    fontSize="EyeBrow"
                    textAlign="center"
                    themeKey="textSecondary"
                >
                    {props.Headline}
                </Text>

                <GridColumn className={containerStyles}>
                    <ul css={logoListStyles}>
                        {props.Logos.map((logo, i) => (
                            <li key={i}>{renderLogo(logo)}</li>
                        ))}
                    </ul>
                </GridColumn>
            </GridColumn>
        </GridContainer>
    );
}
